import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { getMyAccountantProfile } from "@/services/authService";

function initialsFromName(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0)
    .slice(0, 2);
  if (parts.length === 0) return "··";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

/**
 * Contador equivalent of the empresa-picker pill on tenant pages. Shows
 * the studio identity (gradient mark + mono uppercase studio name).
 * Renders only when the user is an accountant AND not currently scoped
 * to a tenant; otherwise the regular TenantContextBar takes over.
 */
export function StudioContextBar({
  className,
}: {
  className?: string;
}): JSX.Element | null {
  const { user } = useAuth();
  const location = useLocation();
  const isAccountant =
    user?.accountType === "accountant" || user?.role === "accountant";
  // Only surface the studio tag when the accountant is in their portfolio
  // context (the /accountant* routes). On a client tenant the tenant pill
  // is the right identity indicator instead.
  const onAccountantHome = location.pathname.startsWith("/accountant");

  const { data: profile } = useQuery({
    queryKey: ["accountant", user?.id ?? null, "profile"],
    queryFn: getMyAccountantProfile,
    enabled: isAccountant && onAccountantHome && Boolean(user?.id),
  });

  if (!isAccountant || !onAccountantHome) return null;

  // For solo accountants, just show their own name (no "Estudio …" prefix —
  // not every accountant runs a branded studio). When a studio is configured
  // we show the actual studio name and its logo if uploaded.
  const displayName = (
    user?.displayName ??
    [user?.firstName, user?.lastName].filter(Boolean).join(" ")
  )?.trim();
  const isStudio = profile?.profile?.kind === "studio";
  const label = isStudio
    ? (profile?.profile?.studioName?.trim() ?? displayName ?? "")
    : (displayName ?? "");
  if (!label) return null;
  const initials = initialsFromName(label);
  const logo = isStudio ? (profile?.profile?.studioLogoUrl ?? null) : null;

  return (
    <div className={cn("fx-studio-tag", className)}>
      <span className="mark" aria-hidden>
        {logo ? <img src={logo} alt="" /> : initials}
      </span>
      <span className="name">{label}</span>
    </div>
  );
}
